import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, AlertTriangle, Clock, TrendingUp, Bell, ExternalLink } from 'lucide-react';
import type { IFAlerta } from '../types/financiamento.types';
import type { Prescricao } from '../services/prescricaoEngine';
import { fmtBRLShort } from './IFFiduciaryCards';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const tipoIcon = (tipo: string) => {
  switch (tipo) {
    case 'risco_elevado': return <AlertTriangle className="h-4 w-4 text-destructive" />;
    case 'prazo_critico': return <Clock className="h-4 w-4 text-destructive" />;
    case 'desvio_progresso': return <TrendingUp className="h-4 w-4 text-[hsl(var(--warning))]" />;
    default: return <Bell className="h-4 w-4 text-muted-foreground" />;
  }
};

const tipoLabels: Record<string, string> = {
  risco_elevado: 'Risco Elevado',
  prazo_critico: 'Prazo Crítico',
  desvio_progresso: 'Desvio de Progresso',
  boletim_aprovado: 'Boletim Aprovado',
};

const sevStyles: Record<string, { border: string; bg: string }> = {
  critico: { border: 'border-destructive/20', bg: 'bg-destructive/[0.04]' },
  atencao: { border: 'border-[hsl(var(--warning))]/20', bg: 'bg-[hsl(var(--warning))]/[0.04]' },
  info: { border: 'border-border/40', bg: 'bg-card' },
};

const confiancaLabel: Record<string, { text: string; color: string }> = {
  alta: { text: 'Alta', color: 'text-[hsl(var(--success))]' },
  media: { text: 'Média', color: 'text-[hsl(var(--warning))]' },
  baixa: { text: 'Baixa', color: 'text-muted-foreground' },
};

interface Props {
  alerta: IFAlerta;
  prescricao: Prescricao;
  onMarcarLido?: (id: string) => void;
  isPending?: boolean;
}

export const IFAlertaPrescritivo: React.FC<Props> = ({ alerta, prescricao, onMarcarLido, isPending }) => {
  const navigate = useNavigate();
  const sev = sevStyles[prescricao.severidade] || sevStyles.info;
  const conf = confiancaLabel[prescricao.confianca] || confiancaLabel.baixa;

  return (
    <div className={`rounded-lg border p-4 ${sev.border} ${sev.bg} transition-colors`}>
      {/* Header: Type + Title */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          {tipoIcon(alerta.tipo)}
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {tipoLabels[alerta.tipo] || alerta.tipo}
          </span>
          <span className="text-sm font-medium truncate">— {alerta.titulo}</span>
        </div>
        <span className="text-[10px] text-muted-foreground shrink-0">
          {formatDistanceToNow(new Date(alerta.created_at), { addSuffix: true, locale: ptBR })}
        </span>
      </div>

      {/* THE PRESCRIPTION — Action first */}
      <div className="mb-3 pl-6">
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-0.5">
          Ação recomendada
        </p>
        <p className="text-sm font-medium text-foreground leading-relaxed">
          {prescricao.acao}
        </p>
      </div>

      {/* Justificativas — "POR QUE ESSA AÇÃO?" */}
      {prescricao.justificativas.length > 0 && (
        <div className="mb-3 pl-6">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-0.5">
            Por quê
          </p>
          <ul className="space-y-0">
            {prescricao.justificativas.map((j, i) => (
              <li key={i} className="text-xs text-foreground/80 flex items-start gap-1.5">
                <span className="text-muted-foreground">•</span>{j}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Impact + Deadline + Confidence row */}
      <div className="flex flex-wrap items-end gap-x-6 gap-y-2 pl-6 mb-3">
        {prescricao.impactoMax > 0 && (
          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Impacto estimado</p>
            <p className="text-sm font-bold text-destructive tabular-nums">
              {fmtBRLShort(prescricao.impactoMin)} – {fmtBRLShort(prescricao.impactoMax)}
            </p>
          </div>
        )}
        <div>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Prazo</p>
          <p className="text-sm font-semibold">{prescricao.prazo}</p>
        </div>
        <div>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Confiança</p>
          <p className={`text-sm font-semibold ${conf.color}`}>{conf.text}</p>
        </div>
      </div>

      {/* Consequence — "O QUE ACONTECE SE NÃO AGIR?" */}
      {prescricao.consequencia && prescricao.severidade !== 'info' && (
        <div className="mb-3 pl-6">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-0.5">
            Se não agir
          </p>
          <p className="text-xs font-medium text-destructive leading-relaxed">{prescricao.consequencia}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 pl-6">
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs gap-1"
          onClick={() => navigate(`/financiamento/contrato/${alerta.contrato_id}`)}
        >
          <ExternalLink className="h-3 w-3" />
          Ver contrato
        </Button>
        {onMarcarLido && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1"
            disabled={isPending}
            onClick={() => onMarcarLido(alerta.id)}
          >
            <Check className="h-3 w-3" />
            Marcar lido
          </Button>
        )}
      </div>
    </div>
  );
};
